/**
 * parts.tsx — byggeklosser til inGPU-illustrasjonene (isometrisk SVG)
 *
 * GpuCard: GPU-kort (mørkt kretskort) med to vifter og et blått kjølelegeme-felt. Ingen
 * produsentlogo eller modellnavn. Gjenbruker Rack/Cube fra inVPS-delene. Kun blå og grå.
 */

import { Box, iso as heroIso } from '@/components/product/iso';

type Project = (x: number, y: number, z: number) => string;

interface GpuCardProps {
  x: number;
  y: number;
  z?: number;
  project?: Project;
}

/** Skala i iso.tsx og i panel-illustrasjonene (px per enhet) */
const S = 34;

/** Isometrisk sirkel (radius r enheter) på toppflaten, sentrert i (cx, cy) i enheter */
function Fan({ cx, cy, z, r, project }: { cx: number; cy: number; z: number; r: number; project: Project }) {
  const [sx, sy] = project(cx, cy, z).split(',').map(Number);
  const rx = r * S * 1.2247;
  const ry = r * S * 0.7071;
  return (
    <g>
      <ellipse cx={sx} cy={sy} rx={rx} ry={ry} fill="#262626" />
      <ellipse cx={sx} cy={sy} rx={rx * 0.78} ry={ry * 0.78} fill="#4589ff" opacity="0.9" />
      <ellipse cx={sx} cy={sy} rx={rx * 0.28} ry={ry * 0.28} fill="#161616" />
      {[0, 60, 120].map((deg) => (
        <ellipse key={deg} cx={sx} cy={sy} rx={rx * 0.74} ry={ry * 0.12} fill="#0043ce" transform={`rotate(${deg} ${sx} ${sy})`} opacity="0.7" />
      ))}
    </g>
  );
}

export function GpuCard({ x, y, z = 0, project = heroIso }: GpuCardProps) {
  const w = 4.2;
  const d = 2.2;
  const h = 0.5;
  const zt = z + h;
  const p = (u: number, v: number) => project(x + u * w, y + v * d, zt);
  return (
    <g>
      <Box x={x} y={y} z={z} w={w} d={d} h={h} colors={['#525252', '#393939', '#262626']} project={project} />
      {/* kjølelegeme: lysere felt på toppflaten */}
      <polygon points={`${p(0.04, 0.08)} ${p(0.96, 0.08)} ${p(0.96, 0.92)} ${p(0.04, 0.92)}`} fill="#6f6f6f" />
      <Fan cx={x + w * 0.27} cy={y + d * 0.5} z={zt} r={0.82} project={project} />
      <Fan cx={x + w * 0.73} cy={y + d * 0.5} z={zt} r={0.82} project={project} />
      {/* kontaktfelt langs kanten */}
      <polygon points={`${p(0.12, 0.96)} ${p(0.62, 0.96)} ${p(0.62, 1.04)} ${p(0.12, 1.04)}`} fill="#8d8d8d" />
    </g>
  );
}
